import { Box, Grid, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { FC } from 'react'
import { useFormContext } from 'react-hook-form'

// others
import { UserRole } from '../../../../../generated'
import UserDropdownWithForm from '../../../common/UserDropDownWithForm'

/**
 * "Consult Attorney" — the attorney who took the consult.
 *
 * Rendered at the top of the Legal Team step but deliberately kept OUTSIDE the `legalTeam` field array: that
 * array is driven by `reorderLegalTeam` and persisted as `CaseToUsers` roles, both of which are read by the
 * legal-team displays and by reports that do EXISTS over those roles. This value is a plain
 * `Cases.consultAttorneyId` FK instead, so nothing existing sees it.
 *
 * Boxed off from the roster below because it is a different kind of thing: the roles in the field array are
 * the team assigned to the work, this is a record of who actually took the consultation. Users were reading
 * it as just another role.
 *
 * Consults only. `isConsultMatter` is a form value in BOTH the create form (from the route) and the edit form
 * (from the loaded matter), so returning null here hides it for regular matters in both places — and because
 * the field is never registered for a matter, it also cannot contribute a validation error there.
 */
export const ConsultAttorney: FC = () => {
  const { watch } = useFormContext<{ isConsultMatter?: boolean | null }>()

  if (!watch('isConsultMatter')) return null

  return (
    <Box
      sx={{
        // Brand maroon, softened. At full strength a 1px #771117 rule reads as an error state; at 40%
        // it still says "this block is different" without shouting. Derived from the palette rather
        // than hard-coded so it follows primary.main if the brand colour ever moves.
        backgroundColor: theme => alpha(theme.palette.primary.main, 0.02),
        borderRadius: 1,
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: theme => alpha(theme.palette.primary.main, 0.4)
      }}
    >
      <Typography variant="subtitle2" marginBottom={1}>
        Consult
      </Typography>

      {/* No `spacing` here on purpose: with a single field it buys nothing and its negative margins
          fight the Box padding. */}
      <Grid container>
        <Grid item xs={12} md={8}>
          {/* Stores the user id as a plain string, the same contract as the existing `openedBy` dropdown. */}
          <UserDropdownWithForm
            name="consultAttorneyId"
            label="Consult Attorney"
            roles={[UserRole.Attorney]}
            sx={{ width: '100%' }}
          />
        </Grid>
      </Grid>

      {/* The persistence claim is real: convertToNormalMatter only rewrites isConsultMatter, the matter
          number and the conversion audit columns, and updateCase writes consultAttorneyId only when the key
          is explicitly present — so conversion leaves it alone, and the matter detail card keeps showing the
          row once convertedToNormalMatter is set. */}
      <Typography variant="caption" color="text.secondary" display="block" marginTop={1.5}>
        The attorney who is actually doing the consultation. This is separate from the legal team roles below,
        and the value is kept even if this consult is later converted to a regular matter.
      </Typography>
    </Box>
  )
}
